import {
  useRef,
  useEffect,
  useState,
  type ReactNode,
  type CSSProperties,
} from "react";

interface RevealProps {
  children: ReactNode;
  /** delay in ms before the animation starts */
  delay?: number;
  /** vertical translate offset in px when hidden */
  y?: number;
  /** transition duration in ms */
  duration?: number;
  className?: string;
  /** only animate once when it first enters the viewport */
  once?: boolean;
  /** intersection observer threshold */
  threshold?: number;
}

export default function Reveal({
  children,
  delay = 0,
  y = 40,
  duration = 900,
  className = "",
  once = true,
  threshold = 0.15,
}: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect prefers-reduced-motion
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(el);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -60px 0px" },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold]);

  const style: CSSProperties = {
    transform: visible ? "translate3d(0, 0, 0)" : `translate3d(0, ${y}px, 0)`,
    opacity: visible ? 1 : 0,
    transition: `transform ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms, opacity ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`,
    willChange: "transform, opacity",
  };

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  );
}